import { css } from '@emotion/react'
import {
  Button,
  Cell,
  DropdownDivider,
  DropdownItem,
  Grid,
  Heading,
  HFlow,
  Icon,
  Tag,
  Text,
  Tooltip,
  VFlow,
} from 'bold-ui'
import { ButtonSize } from 'bold-ui/lib/components/Button/Button/ButtonSkins'
import { useAlert } from 'components/alert'
import { useSession } from 'components/auth/useSession'
import { DateTime } from 'components/date'
import { DropdownButton } from 'components/dropdown'
import { Ellipsis } from 'components/Ellipsis'
import { useErrorHandler } from 'components/error'
import { HLabel } from 'components/HLabel'
import { Telefone } from 'components/label'
import { CIDADAO_PATH } from 'components/layout/SideMenu/SideMenuItems'
import { confirm } from 'components/modals/confirm'
import { HideOnScreenSize } from 'components/responsive'
import {
  CidadaoVisualizacaoDocument,
  useExcluirCidadaoMutation,
  useRecusarCadastroCidadaoMutation,
} from 'graphql/hooks.generated'
import { Fragment, useState } from 'react'
import { useHistory, useLocation } from 'react-router'
import { identidadeGeneroRecord, sexoRecord } from 'types/enums'
import Permissions from 'types/Permissions'
import { humanizeAge } from 'util/date/humanize-age'
import { JustificativaVisualizarProntuarioModal } from 'view/prontuario/justificativa/modal'
import { ImovelUrlParams } from 'view/visualizacao-imovel/model-visualizacao-imovel'

import { grupoCboVisualizarProntuario } from '../../authorization/acessos'
import { useSaudeIndigenaAuthorization } from '../../authorization/useSaudeIndigenaAuthorization'
import { CodigoIdentificadorCidadao } from '../../components/CodigoIdentificadorCidadao'
import { ModalInformarLoginGovBr } from '../../components/ModalInformarLoginGovBr'
import { ModalRecusaCadastro } from '../../components/ModalRecusaCadastro'
import { useNavigateToCidadaoCallbackUrl } from '../../hooks/useNavigateToCidadaoCallbackUrl'
import { useNeedLoginGovBrToCadsus } from '../../hooks/useNeedLoginGovBrToCadsus'
import { CidadaoCabecalho } from '../types/model-cidadaoCabecalho'

export interface CidadaoCabecalhoSectionProps {
  cidadao: CidadaoCabecalho
  dataAtendimento?: number
  sizeBotaoAtualizar?: ButtonSize
  botaoVerProntuario?: boolean
  botaoVerAgendamentos?: boolean
  botaoExcluirCidadao?: boolean
  botaoSelecionar?: boolean
  botaoRetornar?: boolean
  callbackUrl?: string
  callbackParams?: string
  cadImovelCallBackParams?: ImovelUrlParams
}

export const CidadaoCabecalhoSection = (props: CidadaoCabecalhoSectionProps) => {
  return (
    <HFlow
      alignItems='flex-start'
      justifyContent='space-between'
      style={css`
        margin-top: 1rem;
      `}
    >
      <InformacoesCadastraisSection {...props} />
      <Acoes
        cidadao={props.cidadao}
        sizeBotaoAtualizar={props.sizeBotaoAtualizar}
        botaoVerProntuario={props.botaoVerProntuario}
        botaoVerAgendamentos={props.botaoVerAgendamentos}
        botaoExcluirCidadao={props.botaoExcluirCidadao}
        botaoSelecionar={props.botaoSelecionar}
        callbackUrl={props.callbackUrl}
        callbackParams={props.callbackParams}
        cadImovelCallBackParams={props.cadImovelCallBackParams}
        botaoRetornar={props.botaoRetornar}
      />
    </HFlow>
  )
}

export const CidadaoNomeSection = (props: CidadaoCabecalhoSectionProps) => {
  const { cidadao } = props

  return (
    <Grid alignItems='baseline' gap={0.5} wrap={false}>
      <Cell>
        {cidadao?.nomeSocial ? (
          <Heading data-cy='CidadaoCabecalho.nomeSocial' level={2}>
            {cidadao.nomeSocial.titleCase()}
            <Text css={styles.cidadaoNomeSectionBold}> (Nome social)</Text>{' '}
            <Text css={styles.cidadaoNomeSectionNormal} data-cy='CidadaoCabecalho.nome'>
              {cidadao?.nome.titleCase()}
            </Text>
            {cidadao?.cidadaoAldeado?.nomeTradicional && (
              <Text css={styles.cidadaoNomeSectionNormal} data-cy='CidadaoCabecalho.nomeTradicional'>
                {' | '}
                {cidadao.cidadaoAldeado.nomeTradicional.titleCase()}
              </Text>
            )}
            <Text css={styles.cidadaoNomeSectionNormal} data-cy='CidadaoCabecalho.sexo'>
              {' | Sexo '}
              {sexoRecord[cidadao?.sexo]}
            </Text>{' '}
            {cidadao?.identidadeGeneroDbEnum && (
              <Text css={styles.cidadaoNomeSectionNormal} data-cy='CidadaoCabecalho.identidadeGenero'>
                {`(${identidadeGeneroRecord[cidadao.identidadeGeneroDbEnum]})`}
              </Text>
            )}
          </Heading>
        ) : (
          <Heading data-cy='CidadaoCabecalho.nome' level={2}>
            {cidadao?.nome.titleCase()}
            {cidadao?.cidadaoAldeado?.nomeTradicional && (
              <Text css={styles.cidadaoNomeSectionNormal} data-cy='CidadaoCabecalho.nomeTradicional'>
                {' | '}
                {cidadao.cidadaoAldeado.nomeTradicional.titleCase()}
              </Text>
            )}
            <Text css={styles.cidadaoNomeSectionNormal} data-cy='CidadaoCabecalho.sexo'>
              {' | Sexo '}
              {sexoRecord[cidadao?.sexo]}
            </Text>{' '}
            {cidadao?.identidadeGeneroDbEnum && (
              <Text css={styles.cidadaoNomeSectionNormal} data-cy='CidadaoCabecalho.identidadeGenero'>
                {`(${identidadeGeneroRecord[cidadao.identidadeGeneroDbEnum]})`}
              </Text>
            )}
          </Heading>
        )}
      </Cell>
      <Cell>
        {cidadao?.faleceu && (
          <Tag style={styles.tag} data-cy='CidadaoCabecalho.marcacaoObito'>
            Óbito
          </Tag>
        )}
        {!cidadao?.ativo && (
          <Tag style={styles.tag} data-cy='CidadaoCabecalho.marcacaoInativo'>
            Inativo
          </Tag>
        )}
        {cidadao?.unificado && (
          <Tag style={styles.tag} data-cy='CidadaoCabecalho.marcacaoCadastroUnificado'>
            Cadastro unificado
          </Tag>
        )}
        {cidadao?.unificacaoBase && (
          <Tooltip text={`Cadastro originado após unificação da Base ${cidadao.unificacaoBase}`}>
            <Tag style={styles.tag} data-testid='CidadaoCabecalho.marcacaoOrigemBaseUnificada'>
              Base {cidadao.unificacaoBase.titleCase()}
            </Tag>
          </Tooltip>
        )}
      </Cell>
    </Grid>
  )
}

const IdadeSection = (props: { cidadao: CidadaoCabecalho; dataAtendimento: number }) => {
  const { cidadao, dataAtendimento } = props

  return (
    cidadao?.dataNascimento && (
      <Text
        style={css`
          white-space: nowrap;
        `}
      >
        {humanizeAge(cidadao.dataNascimento, dataAtendimento)}
        {' | Nasceu em '}
        <DateTime format='DD/MM/YYYY' value={cidadao.dataNascimento} data-cy='CidadaoCabecalho.dataNascimento' />
      </Text>
    )
  )
}

const InformacoesCadastraisSection = (props: CidadaoCabecalhoSectionProps) => {
  const { cidadao, dataAtendimento } = props
  const telefone = cidadao?.telefoneCelular || cidadao?.telefoneResidencial || cidadao?.telefoneContato

  return (
    <Grid gapVertical={0}>
      <Cell size={12}>
        <CidadaoNomeSection {...props} />
      </Cell>

      <Cell size={12}>
        <Grid wrap={false}>
          <Cell>
            <VFlow vSpacing={0}>
              <IdadeSection cidadao={cidadao} dataAtendimento={dataAtendimento} />
              <HLabel title='Telefone' data-cy='CidadaoCabecalho.telefone'>
                <Telefone value={telefone} />
              </HLabel>
            </VFlow>
          </Cell>
          <Cell>
            <VFlow vSpacing={0}>
              <HFlow hSpacing={1}>
                <CodigoIdentificadorCidadao cpf={cidadao?.cpf} cns={cidadao?.cns} dataCyPrefix='CidadaoCabecalho' />
                <Ellipsis title={cidadao?.nomeMae?.titleCase()} maxLines={1}>
                  <HLabel title={<NomeMaeTitle />} data-cy='CidadaoCabecalho.nomeMae'>
                    {cidadao?.nomeMae?.titleCase()}
                  </HLabel>
                </Ellipsis>
              </HFlow>
              <Ellipsis title={cidadao?.cidadaoVinculacaoEquipe?.unidadeSaude?.nome.titleCase()} maxLines={3}>
                <HLabel title='Unidade responsável' data-cy='CidadaoCabecalho.unidadeResponsavel'>
                  {cidadao?.cidadaoVinculacaoEquipe?.unidadeSaude?.nome.titleCase()}
                </HLabel>
              </Ellipsis>
            </VFlow>
          </Cell>
        </Grid>
      </Cell>
    </Grid>
  )
}

function NomeMaeTitle() {
  return (
    <Fragment>
      <HideOnScreenSize up='lg'>Mãe</HideOnScreenSize>
      <HideOnScreenSize down='lg'>Nome da mãe</HideOnScreenSize>
    </Fragment>
  )
}

export const Acoes = (props: CidadaoCabecalhoSectionProps) => {
  const {
    cidadao,
    sizeBotaoAtualizar,
    botaoVerProntuario,
    botaoVerAgendamentos,
    botaoExcluirCidadao,
    botaoSelecionar,
    botaoRetornar,
    callbackUrl,
    callbackParams,
    cadImovelCallBackParams,
  } = props
  const { hasAuthorization, hasCboAuth } = useSession()
  const { hasCadastrarEditarAldeadoPermission } = useSaudeIndigenaAuthorization()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalRecusaCadastroOpen, setModalRecusaCadastroOpen] = useState(false)

  const cadastrarEEditar = hasAuthorization(Permissions.gestaoDeCadastros.visualizarCadastroIndividual.cadastrarEEditar)
  const excluir = hasAuthorization(Permissions.gestaoDeCadastros.visualizarCadastroIndividual.excluir)
  const visualizarProntuario =
    hasAuthorization(Permissions.visualizarProntuario) && hasCboAuth(grupoCboVisualizarProntuario)
  const visualizarAgenda = hasAuthorization(Permissions.visualizarAgenda)

  const showBotaoVerProntuario = visualizarProntuario && botaoVerProntuario
  const showBotaoVerAgendamentos = visualizarAgenda && botaoVerAgendamentos
  const showBotaoExcluirCidadao = excluir && botaoExcluirCidadao

  const [excluirCidadao] = useExcluirCidadaoMutation()
  const [recusarCadastroCidadao] = useRecusarCadastroCidadaoMutation()
  const handleRejection = useErrorHandler()
  const alert = useAlert()

  const history = useHistory()
  const location = useLocation()

  const navigateToCidadaoCallbackUrl = useNavigateToCidadaoCallbackUrl()
  const handleOnConcluir = (cidadaoId: string) => {
    sessionStorage.removeItem('cidadaoCadsus')
    if (callbackUrl) {
      navigateToCidadaoCallbackUrl(cidadaoId)
    } else {
      history.push(`${CIDADAO_PATH}/${cidadaoId}`)
    }
  }

  const handleExcluir = () => {
    confirm({
      title: `Deseja excluir o cadastro individual?`,
      confirmLabel: 'Excluir',
      type: 'danger',
      onConfirm: () => {
        return excluirCidadao({ variables: { cidadaoId: cidadao?.id } })
          .then(() => {
            alert('success', 'Cadastro individual excluído.')
            history.push(CIDADAO_PATH)
          })
          .catch(handleRejection)
      },
    })()
  }

  const handleRecusaCadastro = () => {
    return recusarCadastroCidadao({
      variables: { cidadaoId: cidadao.id },
      refetchQueries: [{ query: CidadaoVisualizacaoDocument, variables: { id: cidadao.id } }],
      awaitRefetchQueries: true,
    })
      .then(() => {
        alert('success', 'Recusa de cadastro registrada.')
        setModalRecusaCadastroOpen(false)
      })
      .catch(handleRejection)
  }

  const showBotaoAtualizarCadastro = !location.pathname.includes('atendimento-vacinacao')
  const showBotaoRecusaAtualizacaoCadastral = cadastrarEEditar && showBotaoAtualizarCadastro

  const onVisualizarProntuarioClick = () => {
    setIsModalOpen(true)
  }

  const handleVerAgendamentosClick = () => {
    cidadao && history.push(`${CIDADAO_PATH}/${cidadao.id}/agendamentos`)
  }

  const { needLoginGovBr: deveAparecerModalGovBr } = useNeedLoginGovBrToCadsus()
  const [modalInformarLoginGovBrOpen, setModalInformarLoginGovBrOpen] = useState(false)

  const continueEditarCidadao = () => {
    if (callbackUrl) {
      if (cadImovelCallBackParams) {
        history.push(
          `${CIDADAO_PATH}/${props.cidadao.id}/edit?callbackUrl=${callbackUrl}&cadImovelUrlParams[redirect]=${
            cadImovelCallBackParams.redirect
          }${cadImovelCallBackParams.endereco ? '&cadImovelUrlParams[endereco]=true' : ''}${
            cadImovelCallBackParams.imovelId ? `&cadImovelUrlParams[imovelId]=${cadImovelCallBackParams.imovelId}` : ''
          }`
        )
      } else {
        history.push(
          `${CIDADAO_PATH}/${props.cidadao.id}/edit?callbackUrl=${callbackUrl}&callbackParams=${callbackParams}`
        )
      }
    } else {
      history.push(`${CIDADAO_PATH}/${props.cidadao.id}/edit${location.search}`)
    }
  }

  const onEditarClick = () => {
    if (deveAparecerModalGovBr) {
      setModalInformarLoginGovBrOpen(true)
    } else {
      continueEditarCidadao()
    }
  }

  const disableEditAldeado = cidadao?.cidadaoAldeado && !hasCadastrarEditarAldeadoPermission

  const tooltipEdit = !cidadao?.ativo
    ? 'Este cadastro não pode ser atualizado pois está inativo'
    : disableEditAldeado && 'É necessário permissão para editar cidadão aldeado'

  return (
    <Fragment>
      {visualizarProntuario && botaoVerProntuario && (
        <JustificativaVisualizarProntuarioModal
          prontuarioId={cidadao?.prontuario?.id}
          cidadaoId={cidadao.id}
          isModalOpen={isModalOpen}
          onReject={() => setIsModalOpen(false)}
        />
      )}
      <VFlow vSpacing={showBotaoAtualizarCadastro ? 0.3 : 2}>
        <HFlow hSpacing={0.5} justifyContent='flex-end'>
          {cadastrarEEditar && showBotaoAtualizarCadastro && (
            <Tooltip text={tooltipEdit}>
              <Button
                disabled={!cidadao?.ativo || disableEditAldeado}
                kind='primary'
                onClick={onEditarClick}
                size={sizeBotaoAtualizar}
                skin='default'
                data-cy='CidadaoCabecalho.atualizarCadastro'
              >
                Atualizar cadastro
              </Button>
            </Tooltip>
          )}
          {(showBotaoVerProntuario ||
            showBotaoVerAgendamentos ||
            showBotaoExcluirCidadao ||
            showBotaoRecusaAtualizacaoCadastral) && (
            <DropdownButton kind='normal' size={sizeBotaoAtualizar} skin='outline' data-cy='CidadaoCabecalho.dropDown'>
              {showBotaoRecusaAtualizacaoCadastral && (
                <DropdownItem
                  data-cy='CidadaoCabecalho.botaoRecusaAtualizacaoCadastral'
                  onClick={() => setModalRecusaCadastroOpen(true)}
                >
                  Recusa de atualização cadastral
                </DropdownItem>
              )}

              {showBotaoVerProntuario && (
                <Tooltip text={!cidadao?.prontuario && 'Cadastro não possui prontuário'}>
                  <DropdownItem
                    data-cy='CidadaoCabecalho.botaoVerProntuario'
                    onClick={onVisualizarProntuarioClick}
                    disabled={!cidadao?.prontuario}
                  >
                    Ver prontuário
                  </DropdownItem>
                </Tooltip>
              )}

              {showBotaoVerAgendamentos && (
                <Tooltip text={!cidadao?.possuiAgendamento && 'Não há agendamentos registrados para o cidadão'}>
                  <DropdownItem
                    data-cy='CidadaoCabecalho.botaoVerAgendamentos'
                    onClick={handleVerAgendamentosClick}
                    disabled={!cidadao?.possuiAgendamento}
                  >
                    Ver agendamentos
                  </DropdownItem>
                </Tooltip>
              )}
              {showBotaoExcluirCidadao && (
                <Fragment>
                  {(showBotaoVerProntuario || showBotaoVerAgendamentos || showBotaoRecusaAtualizacaoCadastral) && (
                    <DropdownDivider />
                  )}
                  <DropdownItem
                    type='danger'
                    style={{
                      paddingTop: '0.25rem',
                      paddingBottom: '0.25rem',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    onClick={handleExcluir}
                    data-cy='CidadaoCabecalho.botaoExcluir'
                  >
                    <Icon icon='trashOutline' style={{ marginRight: '0.5rem' }} /> Excluir
                  </DropdownItem>
                </Fragment>
              )}
            </DropdownButton>
          )}
        </HFlow>
        <HFlow hSpacing={0.2} alignItems='center' data-cy='CidadaoCabecalho.dataUltimaAtualizacao'>
          <Text>Última atualização em</Text>
          <DateTime format='DD/MM/YYYY' value={cidadao?.dataAtualizado} />
        </HFlow>
        {botaoSelecionar && !botaoRetornar && (
          <HFlow hSpacing={0.2} justifyContent='flex-end'>
            <Button kind='normal' skin='outline' size='small' onClick={() => handleOnConcluir(props.cidadao.id)}>
              <Icon icon='plus' style={{ marginRight: '0.5rem' }} />
              Selecionar e voltar
            </Button>
          </HFlow>
        )}
        <ModalInformarLoginGovBr
          open={modalInformarLoginGovBrOpen}
          onClose={() => setModalInformarLoginGovBrOpen(false)}
          onConfirm={continueEditarCidadao}
        />
        <ModalRecusaCadastro
          open={modalRecusaCadastroOpen}
          onClose={() => setModalRecusaCadastroOpen(false)}
          onConfirm={handleRecusaCadastro}
          isAtualizacao
        />
      </VFlow>
    </Fragment>
  )
}

const styles = {
  tag: css`
    &:not(:last-of-type) {
      margin-right: 0.5rem;
      margin-bottom: 0.5rem;
    }
  `,
  cidadaoNomeSectionBold: css`
    font-size: 1rem;
    font-weight: bold;
  `,
  cidadaoNomeSectionNormal: css`
    font-size: 0.9rem;
    font-weight: normal;
  `,
}
